import { IBaseAlertProps } from "@components/alert/Alert.types";
import { getFileNameFromResponseHeaders } from "@components/fileUploader/File.utils";
import { isODataError, ODataError } from "@odata/Data.types";
import { IFileMetadataEntity } from "@odata/GeneratedEntityTypes";
import { parseError, parseResponse } from "@odata/ODataParser";
import customFetch from "@utils/customFetch";
import { logger } from "@utils/log";
import { saveAs } from "file-saver";

import { FILES_API_URL } from "../constants";
import { getAlertFromError } from "../views/formView/Form.utils";
import { roundToDecimalPlaces } from "./general";

export interface IFileProgress {
    /** Size of the file */
    total: number;
    /** Size of the currently downloaded/uploaded part */
    loaded: number;
    percent: number;
    finished: boolean;
    name?: string;
}

export type TFileProgressEvent = (event: IFileProgress) => void;

interface IDownloadArgs {
    fileName?: string;
    progressCallback?: TFileProgressEvent;
}

export interface IFileDownload {
    // custom endpoint
    url?: string;
    progressCallback?: TFileProgressEvent;
    fileName?: string;
}

export interface IFileUpload {
    // custom endpoint
    url?: string;
    file: File,
    name?: string;
    progressCallback?: TFileProgressEvent
}

export default class FileStorage {
    /** Downloads the file and returns it as File object. Can be used to create custom downloader with progress tracker. */
    static async get(id?: number, args: IFileDownload = {}): Promise<File> {
        let url = args?.url ?? FILES_API_URL;

        if (id) {
            url += `/${id}`;
        }

        const response = await fetch(url);

        if (!response.ok) {
            throw new Error(`Error while fetching a file: ${response.status}, ${response.statusText}`);
        }
        return getFileFromSuccessResponse({ progressCallback: args.progressCallback, fileName: args.fileName }, response);
    }

    /** Opens saveAs dialog for the file, browser will handle the download. */
    static download(id: number): void {
        downloadFileFromUrl(`${FILES_API_URL}/${id}`);
    }

    static async upload(args: IFileUpload): Promise<IFileMetadataEntity> {
        // use XMLHttpRequest instead of fetch to provide progress event

        return new Promise((resolve, reject) => {
            const request = new XMLHttpRequest();
            const fileName = encodeURIComponent(args.name ?? args.file.name);

            request.open("POST", args.url ?? FILES_API_URL);
            request.setRequestHeader("Content-Disposition", `form-data;filename="${fileName}"`);

            if (args.file.type) {
                request.setRequestHeader("Content-Type", args.file.type);
            }

            if (args.progressCallback) {
                request.upload.onprogress = (e) => {
                    const percentComplete = (e.loaded / e.total) * 100;

                    args.progressCallback({
                        name: fileName,
                        total: e.total,
                        loaded: e.loaded,
                        percent: roundToDecimalPlaces(2, percentComplete),
                        finished: e.total === e.loaded
                    });
                };
            }

            request.onload = (e) => {
                if (request.status < 200 || request.status >= 300) {
                    try {
                        reject(parseError(JSON.parse(request.responseText)));
                    } catch (e) {
                        reject(new Error(`Error while uploading a file: ${request.status}, ${request.statusText}`));
                    }
                }

                resolve(JSON.parse(request.response));
            };

            request.onerror = (event) => {
                reject(new Error(`Uknown error while uploading a file`));
            };

            request.send(args.file);
        });
    }

    static async delete(id: number): Promise<Response> {
        return fetch(`${FILES_API_URL}/${id}`, {
            method: "DELETE"
        });
    }
}

export const getFileStorageUploadErrorMessage = (error: ODataError | Error): string => {
    // error thrown from FileStorage.upload can be either oDataError or default Error (with message) when there
    // is complete unknown BE error thrown
    return (error as ODataError)._validationMessages?.[0]?.message ?? (error as ODataError)._message ?? (error as Error).message;
};

export async function getFileFromSuccessResponse(args: IDownloadArgs, response: Response): Promise<File> {
    const reader = response.body.getReader();
    const type = response.headers.get("Content-Type");
    const totalSize = +response.headers.get("Content-Length");
    let receivedLength = 0; // received that many bytes at the moment
    const chunks = [];
    while (true) {
        const { done, value } = await reader.read();
        if (done) {
            break;
        }
        chunks.push(value);
        if (args?.progressCallback) {
            receivedLength += value.length;
            const percentComplete = (receivedLength / totalSize) * 100;
            args.progressCallback({
                total: totalSize,
                loaded: receivedLength,
                percent: roundToDecimalPlaces(2, percentComplete),
                finished: totalSize === receivedLength
            });
        }
    }
    const fileNameFromHeaders = getFileNameFromResponseHeaders(response.headers);
    return new File(chunks, args?.fileName ?? fileNameFromHeaders, { type });
}

export async function parseFileResponse(response: Response, fileName?: string): Promise<ODataError> {
    const ret = await parseResponse(response, {
        customSuccessParser: getFileFromSuccessResponse.bind(null, { fileName })
    });
    if (isODataError(ret)) {
        return ret;
    }
    saveAs(ret);
    return null;
}

export async function saveFile(url: string, fileName?: string): Promise<IBaseAlertProps | null> {
    try {
        const response = await customFetch(url);
        const error = await parseFileResponse(response, fileName);
        if (error) {
            return getAlertFromError(error);
        }
    } catch (e) {
        logger.error("failed file save", e);
        return getAlertFromError(e);
    }
    // success, file is downloaded
    return null;
}

interface IDownloadFileFromUrlArgs {
    setBusy?: (busy: boolean) => void;
    fileName?: string;
    setAlert: (alert: IBaseAlertProps) => void;
}

export const downloadFileFromUrl = async (url: string, args?: IDownloadFileFromUrlArgs): Promise<void> => {
    args?.setBusy?.(true);
    // eslint-disable-next-line no-console
    console.info("Downloading file from url:", url, args?.fileName ? ` with name: ${args?.fileName}` : "");
    const alert = await saveFile(url, args?.fileName);
    if (alert) {
        logger.error(`Error while generating client was trying to download a file ${args.fileName} from ${url}`);
        if (args?.setAlert) {
            args.setAlert(alert);
        } else {
            console.error(alert);
        }
    }
    args?.setBusy?.(false);
};